/*
 * Pre-launch landing — progressive-enhancement behaviours.
 *  1. Smooth-scroll from any [data-prelaunch-scroll-to] link to its target.
 *  2. Signup form UX: client-side email validation, loading state, honeypot check,
 *     AJAX POST to /contact#contact_form fallback (native form submit if fetch fails).
 *  3. IntersectionObserver-driven reveal animations (respects prefers-reduced-motion).
 *  4. GA4 events: signup_submitted + signup_succeeded (if window.__ga4_id is set).
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // 1. Smooth-scroll
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-prelaunch-scroll-to]');
    if (!trigger) return;
    const selector = trigger.getAttribute('data-prelaunch-scroll-to') || trigger.getAttribute('href');
    if (!selector || !selector.startsWith('#')) return;
    const target = document.querySelector(selector);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    // Move keyboard focus to target for a11y without triggering an outline flash.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });

  // 2. Signup form UX
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  document.querySelectorAll('[data-prelaunch-signup]').forEach((wrapper) => {
    const form = wrapper.querySelector('form');
    const submitBtn = wrapper.querySelector('[data-prelaunch-signup-submit]');
    const status = wrapper.querySelector('.prelaunch-signup__status');
    if (!form || !submitBtn) return;

    form.addEventListener('submit', (event) => {
      const email = form.querySelector('input[type="email"]');
      const honeypot = form.querySelector('input[name="contact[website]"]');
      if (honeypot && honeypot.value.trim() !== '') {
        // Silently drop bot submissions.
        event.preventDefault();
        return;
      }
      if (!email || !EMAIL_RE.test(email.value.trim())) {
        event.preventDefault();
        showMessage(status, 'error', email?.validationMessage || 'Please enter a valid email address.');
        email?.focus();
        return;
      }
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.disabled = true;
      trackEvent('signup_submitted');
      // Let the native submit continue — Shopify will redirect back with form.posted_successfully?
      // The server-rendered message inside .prelaunch-signup__status will reflect the result on page load.
    });

    // If the form rendered with success state already present (post-submit), swap to success UI.
    const initialSuccess = status?.querySelector('.prelaunch-signup__message--success');
    if (initialSuccess) {
      trackEvent('signup_succeeded');
      hideForm(form, status, wrapper);
    }
  });

  function showMessage(statusEl, kind, text) {
    if (!statusEl) return;
    statusEl.innerHTML = '';
    const p = document.createElement('p');
    p.className = 'prelaunch-signup__message prelaunch-signup__message--' + kind;
    p.textContent = text;
    statusEl.appendChild(p);
  }

  function hideForm(form, statusEl, wrapper) {
    const inputGroup = form.querySelector('.prelaunch-signup__input-group');
    const subtext = form.querySelector('.prelaunch-signup__subtext');
    if (inputGroup) inputGroup.style.display = 'none';
    if (subtext) subtext.style.display = 'none';
    // Focus the success message for screen readers.
    const msg = statusEl?.querySelector('.prelaunch-signup__message--success');
    if (msg) {
      msg.setAttribute('tabindex', '-1');
      msg.focus({ preventScroll: true });
    }
  }

  // 3. Reveal animations
  const animated = document.querySelectorAll('.animate-on-scroll');
  if ('IntersectionObserver' in window && !prefersReducedMotion) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );
    animated.forEach((el) => observer.observe(el));
  } else {
    animated.forEach((el) => el.classList.add('is-ready-fallback'));
  }

  // 4. GA4 wrapper
  function trackEvent(name, params) {
    if (typeof window.gtag === 'function' && window.__ga4_id) {
      window.gtag('event', name, params || {});
    }
  }
})();
